import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  THUMBNAIL_UNSUPPORTED_ERROR,
  THUMBNAIL_UNSUPPORTED_MESSAGE,
} from '@stores/MomentThumbnail/MomentThumbnailStore';
import classNames from 'classnames';
import React from 'react';
import { useMomentThumbnail } from '../../hooks/useMomentThumbnail';
import css from './MomentThumbnail.module.scss';

interface MomentThumbnailProps {
  libraryId: string;
  mediaId: string;
  timestamp: number;
  width?: number;
  height?: number;
  isAudio?: boolean;
}

export const MomentThumbnail: React.FC<MomentThumbnailProps> = ({
  libraryId,
  mediaId,
  timestamp,
  width = 320,
  height = 180,
  isAudio,
}) => {
  // Audio has no frame to grab, and asking for one wastes a capture slot on a
  // seek that can only fail. `enabled` keeps the hook call unconditional.
  //
  // Ask for roughly what is displayed rather than letting the capture default to
  // its 1280px ceiling — 2x the rendered width covers hidpi and keeps these out of
  // the cache's largest bracket. See the note on `maxWidth` in the hook.
  const { thumbnail, isLoading, error } = useMomentThumbnail(libraryId, mediaId, timestamp, {
    enabled: !isAudio,
    maxWidth: width * 2,
  });

  if (isAudio) {
    return (
      <Box className={classNames(css.audioContainer)} style={{ width, height }}>
        <AudioSelectedIcon size={48} />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className={classNames(css.loadingContainer)} style={{ width, height }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    const isUnsupported = error === THUMBNAIL_UNSUPPORTED_ERROR;
    return (
      <Box
        className={classNames(isUnsupported ? css.safariContainer : css.errorContainer)}
        style={{ width, height }}
      >
        <Typography
          className={isUnsupported ? css.safariMessage : undefined}
          color={isUnsupported ? 'textSecondary' : 'error'}
          variant="body2"
        >
          {isUnsupported ? THUMBNAIL_UNSUPPORTED_MESSAGE : error}
        </Typography>
      </Box>
    );
  }

  if (!thumbnail) {
    return null;
  }

  return (
    <img
      src={thumbnail}
      alt={`Moment thumbnail for ${libraryId}:${mediaId} at ${timestamp}s`}
      title={`${libraryId}:${mediaId}:${timestamp}`}
      className={classNames(css.thumbnail)}
    />
  );
};
